import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import { CacheKeys } from '../lib/cache'
import { toCamelCase } from '../lib/typeHelper'
import { type Document } from './document'
import { type InsuranceCoverage, type InsuranceOneTimeFee, Vendor } from './insuranceTypes'

export { Vendor }

export enum PolicyStatus {
  ACTIVE = 'ACTIVE',
  UPCOMING = 'UPCOMING',
  EXPIRING = 'EXPIRING',
  EXPIRED = 'EXPIRED',
  INACTIVE = 'INACTIVE',
  /** In force, canceled as of a date in the next 30 days. */
  CANCELING = 'CANCELING',
  CANCELED = 'CANCELED',
}

export enum CoiMode {
  AUTOMATIC = 'AUTOMATIC',
  MANUAL = 'MANUAL',
}

export interface CorgiPolicyLink {
  policyNumber: string
  url: string
}

export interface InsurancePolicy {
  id: string
  idShort: string
  dateCreated: string
  dateUpdated: string
  vendor: Vendor
  vendorId: string
  /** The carrier that writes the risk — the real carrier for broker vendors. */
  vendorName: string
  /** Set when the policy came through a wholesaler (e.g. Rocklake). */
  broker: string | null
  /** Assigned InsuranceCarrier record; null falls back to vendor branding. */
  carrierId: string | null
  label: string
  dateStart: string
  dateEnd: string
  /** The date the whole policy was canceled as of. Optional for deploy skew. */
  dateCanceled?: string | null
  /** Whether the policy ends on a cancellation, however far off. Optional for deploy skew. */
  isCanceled?: boolean
  org: string
  application: string
  quote: string | null
  policyDocument: Document | null
  coverages: InsuranceCoverage[]
  oneTimeFees: InsuranceOneTimeFee[]
  premium: number
  /** Optional: absent when the frontend is ahead of the backend mid-deploy. */
  premiumExcludingFees?: number
  summary: string | null
  /** Optional: absent when the frontend is ahead of the backend mid-deploy. */
  coiMode?: CoiMode
  isActive: boolean
  status: PolicyStatus
  iconUrl: string
  iconAlt: string
  /** Links into Corgi's broker portal; the server sends them to Cai admins only. */
  corgiPolicyLinks?: CorgiPolicyLink[]
}

export function setPolicyCoiMode(
  orgId: string,
  policyId: string,
  coiMode: CoiMode
): Promise<InsurancePolicy> {
  return axios
    .post(`/api/v1/insure/organisations/${orgId}/policies/${policyId}/coi-mode/`, { coi_mode: coiMode })
    .then((response) => toCamelCase(response.data) as InsurancePolicy)
}

/** Cancels the whole policy, or only coverageIds, as of cancelDate (YYYY-MM-DD). Cai admins only. */
export function cancelPolicy(
  orgId: string,
  policyId: string,
  cancelDate: string,
  coverageIds: string[]
): Promise<InsurancePolicy> {
  return axios
    .post(`/api/v1/insure/organisations/${orgId}/policies/${policyId}/cancel/`, {
      cancel_date: cancelDate,
      coverage_ids: coverageIds,
    })
    .then((response) => toCamelCase(response.data) as InsurancePolicy)
}

export enum ChecklistItem {
  POLICY = 'POLICY',
  COI = 'COI',
  WELCOME_PACKAGE = 'WELCOME_PACKAGE',
}

export interface ChecklistEntry {
  item: ChecklistItem
  isCompleted: boolean
  dateCompleted: string | null
  completedByName: string | null
  /** False when staff can't change it: the policy item ticks itself, an automatic COI waits for its certificate. */
  canToggle: boolean
}

/** What is left to do on a policy once it's bound. Cai admins only. */
export interface PostBindChecklist {
  entries: ChecklistEntry[]
  /** The certificate the COI item asks staff to review; null until one states the policy. */
  coiDocumentId: string | null
}

function checklistUrl(orgId: string, policyId: string): string {
  return `/api/v1/insure/organisations/${orgId}/policies/${policyId}/checklist/`
}

const CERTIFICATE_POLL_MS = 10_000

/**
 * How often to refetch the checklist: every 10s while the policy document is in but its COI is not, because
 * the certificate is built in the background. React Query skips the interval while the tab is hidden.
 */
export function certificatePollInterval(checklist: PostBindChecklist | undefined): number | false {
  const entry = (item: ChecklistItem) => checklist?.entries.find((candidate) => candidate.item === item)
  const isAwaitingCertificate =
    !!entry(ChecklistItem.POLICY)?.isCompleted && !entry(ChecklistItem.COI)?.canToggle
  return isAwaitingCertificate ? CERTIFICATE_POLL_MS : false
}

export function usePostBindChecklist(orgId: string, policyId: string) {
  const { data, isLoading } = useQuery({
    queryKey: [CacheKeys.POST_BIND_CHECKLIST, policyId],
    queryFn: () =>
      axios
        .get(checklistUrl(orgId, policyId))
        .then((response) => toCamelCase(response.data) as PostBindChecklist),
    refetchInterval: (query) => certificatePollInterval(query.state.data),
  })
  return { checklist: data, isLoading }
}

export function setChecklistItemCompleted(
  orgId: string,
  policyId: string,
  item: ChecklistItem,
  completed: boolean
): Promise<PostBindChecklist> {
  return axios
    .post(checklistUrl(orgId, policyId), { item, completed })
    .then((response) => toCamelCase(response.data) as PostBindChecklist)
}

export enum PolicyDocumentReviewStatus {
  PENDING = 'PENDING',
  READY = 'READY',
  FAILED = 'FAILED',
}

/** The diff between an uploaded policy document and the policy on file. */
export interface PolicyDocumentReview {
  status: PolicyDocumentReviewStatus
  hasChanges: boolean
  /** Identifies the upload this review belongs to; a later upload issues a new one. */
  token: string
  vendor?: string | null
  coverages?: {
    coverageType: string
    premium: number | null
    limit: number | null
    deductible: number | null
    comprehensiveDeductible?: number | null
    premiumPerUnit?: number | null
  }[]
  warnings?: string[]
}

const REVIEW_POLL_MS = 3000

function getPolicyDocumentReview(orgId: string, policyId: string): Promise<PolicyDocumentReview | null> {
  return axios
    .get(`/api/v1/insure/organisations/${orgId}/policies/${policyId}/policy-document-review/`)
    .then((response) =>
      response.data?.review ? (toCamelCase(response.data.review) as PolicyDocumentReview) : null
    )
}

/** Polls an uploaded document's change review, which an LLM fills in out of band. */
export function usePolicyDocumentReview(
  orgId: string,
  policyId: string | undefined,
  token: string | undefined
) {
  const { data } = useQuery({
    queryKey: [CacheKeys.POLICY_DOCUMENT_REVIEW, orgId, policyId, token],
    queryFn: () => getPolicyDocumentReview(orgId, policyId!),
    enabled: !!policyId && !!token,
    refetchInterval: (query) => {
      // A review under another token was superseded and can never become ours.
      const stored = query.state.data
      if (!stored || stored.token !== token) return false
      return stored.status === PolicyDocumentReviewStatus.PENDING ? REVIEW_POLL_MS : false
    },
  })
  return { review: data?.token === token ? data : null }
}
